import NestedForm from '@stimulus-components/rails-nested-form'
import Uppy from '@uppy/core'
import AwsS3 from '@uppy/aws-s3'
import { withBreadcrumb } from '../../../../../javascript/mixins/breadcrumb'
import { withErrorHandler } from '../../../../../javascript/mixins/error_handler'

// Compose multiple mixins: error handling + breadcrumb tracking
export default class extends withErrorHandler(withBreadcrumb(NestedForm)) {
  static targets = [
    'fileInput',
    'fileText',
    'hiddenInput',
    'hiddenCheckBox',
    'hiddenFields',
    'hiddenFieldsData',
    'listItem',
  ]

  static values = {
    maxFileSize: Number,
    presignUrl: String,
  }

  connect() {
    super.connect()
    if (this.hasFileInputTarget) {
      // Build restrictions object conditionally - omit maxFileSize when unlimited (nil/0)
      const restrictions = {
        allowedFileTypes: ['application/pdf', '.pdf'],
      }
      if (this.maxFileSizeValue) {
        restrictions.maxFileSize = this.maxFileSizeValue
      }

      this.configs = {
        restrictions: restrictions,
      }
      this.uppy = this.initUppy()
      this.initUppyAws()
      this.initUppyUploadSuccess()
    }
    if (this.hasListItemTarget && !this.listItemTarget.hasChildNodes()) {
      this.listItemTarget.remove()
    }
  }

  disconnect() {
    this.uppy.destroy()
  }

  remove(event) {
    if (event.currentTarget.value < this.hiddenCheckBoxTargets.length) {
      this.hiddenCheckBoxTargets.at(parseInt(event.currentTarget.value)).value = true
      this.hiddenFieldsTarget.appendChild(
        this.hiddenFieldsDataTargets.at(parseInt(event.currentTarget.value)),
      )
    }
    super.remove(event)
  }

  uploadFile(event) {
    const files = Array.from(event.target.files)

    files.forEach((file) => {
      try {
        this.uppy.addFile({
          source: 'file input',
          name: file.name,
          type: file.type,
          data: file,
        })
        super.add(event)
      } catch (err) {
        // err.isRestriction means a UI-visible Uppy restriction (size, type)
        // hit — UX layer surfaces it, no need to report. Other throws are
        // genuine failures and go to AppSignal.
        if (!err.isRestriction) {
          this.reportError(err, { source: 'uppy', action: 'addFile', fileName: file.name })
        }
      }
    })
  }

  triggerFileInput() {
    this.fileInputTarget.click()
  }

  initUppy() {
    let uppy = new Uppy({
      id: this.fileInputTarget.id,
      autoProceed: true,
      restrictions: this.configs.restrictions,
    })

    return uppy
  }

  initUppyAws() {
    const presignUrl = this.presignUrlValue
    this.uppy.use(AwsS3, {
      shouldUseMultipart: () => false,
      getUploadParameters(file) {
        const filename = encodeURIComponent(file.meta.name)
        const type = encodeURIComponent(file.meta.type)
        return fetch(`${presignUrl}?filename=${filename}&type=${type}`).then((response) => {
          if (!response.ok) {
            throw new Error(`Server responded with ${response.status}`)
          }
          return response.json()
        })
      },
    })
  }

  initUppyUploadSuccess() {
    // The 'upload-success' event provides two parameters: file (UppyFile) and response (object)
    this.uppy.on('upload-success', (file, response) => {
      // Log success to AppSignal
      this.addBreadcrumb('upload', 'upload-success', {
        fileId: file.id,
        fileName: file.name,
        context: 'attributes-component',
      })

      if (response && response.body && response.body.key) {
        file.meta['key'] = response.body.key
      }
      this.notifyUploadSuccess(file.name)
      this.setHiddenInput(file)
    })

    // The 'upload-error' event provides three parameters: file (UppyFile), error (Error) and response (object)
    this.uppy.on('upload-error', (file, error, response) => {
      // Add breadcrumb for upload error context
      this.addBreadcrumb('upload', 'upload-error', {
        fileId: file.id,
        fileName: file.name,
        fileType: file.type,
        isNetworkError: error.isNetworkError || false,
        responseStatus: response?.status,
        context: 'attributes-component',
        errorMessage: error.message,
      })
    })

    // Track file added events with breadcrumbs
    // The 'file-added' event provides one parameter: file (UppyFile)
    this.uppy.on('file-added', (file) => {
      this.addBreadcrumb('upload', 'file-added', {
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        context: 'attributes-component',
      })
    })

    // Track upload start events with breadcrumbs
    // The 'upload' event provides two parameters: uploadID (string) and files (UppyFile[])
    this.uppy.on('upload', (uploadID, files) => {
      this.addBreadcrumb('upload', 'upload-started', {
        uploadId: uploadID,
        fileCount: files.length,
        context: 'attributes-component',
      })
    })

    // Track completion events
    // The 'complete' event provides one parameter: result (object) with successful and failed arrays
    this.uppy.on('complete', (result) => {
      this.addBreadcrumb('upload', 'complete', {
        successfulCount: result && result.successful ? result.successful.length : 0,
        failedCount: result && result.failed ? result.failed.length : 0,
        context: 'attributes-component',
      })
    })

    // The 'error' event provides one parameter: error (Error)
    // Uppy errors must be manually reported because they don't bubble up
    // to the global error handler or Stimulus's installErrorHandler
    this.uppy.on('error', (error) => {
      this.reportError(error, { source: 'uppy', context: 'attributes-component' })
    })

    // The 'restriction-failed' event provides two parameters: file (UppyFile) and error (Error)
    this.uppy.on('restriction-failed', (file, error) => {
      // Expected user-facing validation — Uppy's Dashboard shows it. Breadcrumb only.
      this.addBreadcrumb('upload', 'restriction-failed', {
        fileName: file?.name,
        error: error.message,
        context: 'attributes-component',
      })
    })
  }

  setHiddenInput(file) {
    // set hidden field value to the uploaded file data so that it's submitted with the form as the attachment
    const fileData = this.uploadedFileData(file)
    this.hiddenInputTargets.at(-1).value = fileData
  }

  notifyUploadSuccess(fileName) {
    this.fileTextTargets.at(-1).value = fileName
  }

  uploadedFileData(file) {
    // construct uploaded file data in the format that Shrine expects
    // Check if key exists
    if (!file.meta || !file.meta['key']) {
      throw new Error('Could not find key in file metadata')
    }

    // Extract the ID from the key
    const match = file.meta['key'].match(/^cache\/(.+)/)
    if (!match) {
      throw new Error(`Invalid key format: ${file.meta['key']}`)
    }

    return JSON.stringify({
      id: match[1], // object key without prefix
      storage: 'cache',
      metadata: {
        size: file.size,
        filename: file.name,
        mime_type: file.type,
      },
    })
  }
}
